import React, {useEffect, useState} from 'react';
import {useNavigate, useParams} from "react-router-dom";
import {AnyCustomModelJson} from "../../api/json/CustomModelJson";
import {CustomModelRequests} from "../../api/requests/CustomModelRequests";
import {useProjectData} from "../../hooks/useProjectData";
import {Chip, Grid, Stack, Typography} from "@mui/material";
import CustomModelDetailSkeleton from "./CustomModelDetailSkeleton";
import {ProjectType} from "../../api/enum/ProjectType";
import ClassifierTemplateUtil from "../../library/ClassifierTemplateUtil";
import {ColumnPredefinedOptionsJson} from "../../api/json/ColumnJson";
import ApiError from "../../api/ApiError"
import QualityCheckSection from "../qualitycheck/QualityCheckSection"
import CustomModelDetailRail from "./CustomModelDetailRail"
import CustomModelActionsMenu from "../models/CustomModelActionsMenu"
import StatBar from "../common/StatBar"
import SpaceBetween from "../common/SpaceBetween"
import ErrorMessage from "../error/ErrorMessage"
import {useBreadcrumbs} from "../layouts/Nav/BreadcrumbsProvider"
import {NAV_LABELS} from "../../library/NavLabels"

function CustomModelDetailPage() {
    const {id, project, setProject} = useProjectData()
    const {customModelId} = useParams()

    const [customModel, setCustomModel] = useState<AnyCustomModelJson>()
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState<ApiError>()

    useBreadcrumbs([
        {label: NAV_LABELS.MODELS, url: `/projects/${id}/models`},
        {label: customModel?.name ?? ""},
    ])

    const navigate = useNavigate()

    useEffect(() => load(), [project])

    const load = () => {
        if (!customModelId) return

        setCustomModel(undefined)
        setLoading(true)
        CustomModelRequests.get(customModelId)
            .then(setCustomModel)
            .catch(setError)
            .finally(() => setLoading(false))
    }

    if (error) {
        return <ErrorMessage error={error}/>
    }

    if (loading || !project) {
        const classCount = project?.type === ProjectType.CLASSIFIER
            ? (ClassifierTemplateUtil.optionsColumns(project.columns)[0] as ColumnPredefinedOptionsJson | undefined)?.predefinedOptions?.length
            : undefined
        return <CustomModelDetailSkeleton classCount={classCount}/>
    }

    if (!customModel) return <>Unexpected behavior</>

    const showQualityCheck = !!customModel.completed && project.stats.numberOfValidationRows > 0

    const stats = customModel.qualityCheck?.stats
    const heroStats = stats && stats.completedTestRuns > 0
        ? [
            {value: `${stats.score}%`, label: "avg score"},
            {value: stats.completedTestRuns, label: "test runs"},
            ...(stats.averageLatency != null ? [{value: `${Math.round(stats.averageLatency / 100) * 100} ms`, label: "avg latency"}] : []),
        ]
        : null

    return (
        <>
            <SpaceBetween sx={{alignItems: "center", mb: 4}}>
                <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
                    <Typography variant="h1">{customModel.name}</Typography>
                    {customModel.id === project.defaultCustomModelId &&
                        <Chip label="Default" size="small" color="primary"/>
                    }
                    {heroStats && <StatBar stats={heroStats}/>}
                </Stack>
                <CustomModelActionsMenu model={customModel}
                                        project={project}
                                        onModelUpdated={model => setCustomModel(model)}
                                        onProjectUpdated={setProject}
                                        didRename={() => load()}
                                        didDelete={() => navigate(`/projects/${project.id}/models`)}
                />
            </SpaceBetween>

            <Grid container spacing={4}>
                <Grid item xs={12} md={9}>
                    {showQualityCheck &&
                        <QualityCheckSection
                            project={project}
                            model={customModel}
                            updateQualityCheck={newEval => {
                                const newCustomModel = {...customModel}
                                newCustomModel.qualityCheck = newEval
                                setCustomModel(newCustomModel)
                            }}
                        />
                    }
                </Grid>

                <Grid item xs={12} md={3}>
                    <CustomModelDetailRail customModel={customModel} projectId={project.id}/>
                </Grid>
            </Grid>
        </>
    );
}

export default CustomModelDetailPage;
